import { computed, ref, watch, watchEffect } from 'vue'

import {
  useCheckUsernameAvailabilityLazyQuery,
  useUpdateAccountUsernameMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

export function useChangeUsername(user: User) {
  const toastStore = useToastStore()
  const webviewStore = useWebviewStore()

  const username = ref(user.username)

  const variables = computed(() => ({ username: username.value }))

  const {
    result,
    load: checkUsernameAvailability,
    loading: checkingAvailability,
  } = useCheckUsernameAvailabilityLazyQuery(variables, {
    fetchPolicy: 'network-only',
  })

  const { mutate: updateAccountUsername, loading } =
    useUpdateAccountUsernameMutation()

  const isAvailable = ref(true)

  const showValidation = computed(
    () => !!username.value.trim().length && username.value !== user.username
  )

  watch(result, () => {
    isAvailable.value = !!result.value?.checkUsernameAvailability
  })

  watchEffect(() => {
    if (username.value) checkUsernameAvailability()
  })

  const canSave = computed(
    () =>
      isAvailable.value && showValidation.value && !checkingAvailability.value
  )

  const save = async () => {
    if (!isAvailable.value) return false

    updateAccountUsername(
      { username: username.value },
      {
        optimisticResponse: {
          __typename: 'Mutation',
          updateAccountUsername: {
            __typename: 'User',
            id: user.id,
            username: username.value,
          },
        },
        update() {
          if (!webviewStore.isActive) return

          webviewStore.postMessage({
            type: NativeActionsEnum.UserUpdated,
            data: { user: { ...user, username: username.value } },
          })
        },
      }
    )

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Your username has been updated',
    })

    return true
  }

  return {
    username,
    isAvailable,
    checkingAvailability,
    showValidation,
    loading,
    canSave,
    save,
  }
}
